import { at, clockLabel, shiftHour, SHIFT_MINUTES } from '../core/clock';
import { Emitter } from '../core/emitter';
import { clamp, roundCents } from '../core/math';
import { Rng } from '../core/rng';
import {
  CHAIR,
  COOK,
  DIFFICULTIES,
  FLOW,
  HOURS,
  INDEX,
  STATIONS,
  TIPS,
  type Difficulty,
} from './config';
import {
  ARCHETYPES,
  FIGHTER_NAMES,
  FIGHT_TOPICS,
  GENERIC_ANGRY,
  GENERIC_HAPPY,
  GENERIC_WAITING,
  GENERIC_WRONG,
  JUKEBOX_SONGS,
  NEW_HOST_LINE,
  pickArchetype,
  type Archetype,
} from './customers';
import { planNight, type NightPlan, type ScheduledEvent } from './director';
import { cookTimes, hashBrownDoneness, waffleStatus, type CookTimes } from './kitchen';
import { ITEM_LABEL, isAllTheWay, itemCount, orderCall, orderSpeech } from './lingo';
import { generateOrder, withoutWaffle } from './orders';
import {
  computeTip,
  evaluateEggs,
  evaluateHashBrowns,
  evaluateWaffle,
  indexDeltaForService,
  indexLevel,
  isPerfect,
  isTicketComplete,
  orderSubtotal,
  ticketIssues,
  ticketQuality,
} from './scoring';
import {
  TOPPINGS,
  type ActionResult,
  type ArchetypeId,
  type ChairOutcome,
  type ChairState,
  type CharacterLook,
  type Customer,
  type DifficultyId,
  type EggStyle,
  type EndReason,
  type FightState,
  type GameEvent,
  type GameResult,
  type GameStats,
  type GrillSlot,
  type HostState,
  type IndexLevel,
  type ItemKind,
  type LineJob,
  type LiveState,
  type Mode,
  type OrderSpec,
  type Seat,
  type SpeakerId,
  type StormState,
  type Ticket,
  type TicketTag,
  type Topping,
  type WaffleIron,
  type WaffleKind,
} from './types';

export interface GameOptions {
  seed: number;
  mode: Mode;
  difficulty: DifficultyId;
  shiftNumber: number;
  /** Gentler opening for someone who has never finished a shift. */
  firstShift: boolean;
  /** What happened at the shift change before this shift, if there was one. */
  calm?: CalmCarryover;
}

export interface ReturningCustomer {
  name: string;
  look: CharacterLook;
  line: string;
}

export interface CalmCarryover {
  calmed: number;
  /** Extra patience for every customer tonight (0.16 = 16% more patient). */
  patienceBonus: number;
  returning: ReturningCustomer[];
}

interface SpawnOptions {
  seatKind?: 'stool' | 'booth' | 'any';
  order?: OrderSpec;
  tags?: TicketTag[];
  coffeeOnly?: boolean;
  patienceMult?: number;
  arriveLine?: string;
  virtualSeat?: boolean;
  look?: CharacterLook;
  name?: string;
  tipMult?: number;
}

interface PendingLine {
  at: number;
  speaker: SpeakerId;
  text: string;
  duration: number;
  mood: 'normal' | 'happy' | 'angry' | 'spooky';
}

const OK: ActionResult = { ok: true };
const fail = (reason: string): ActionResult => ({ ok: false, reason });

const STOOLS = 6;
const BOOTHS = 3;
/** The Host doesn't need a seat. It stands where the counter meets the dark. */
export const HOST_SEAT_ID = -1;

function emptyStats(): GameStats {
  return {
    served: 0,
    perfect: 0,
    walkouts: 0,
    wrong: 0,
    hashBrowns: 0,
    waffles: 0,
    toppings: 0,
    burnt: 0,
    chairsThrown: 0,
    chairsCaught: 0,
    perfectCatches: 0,
    unbothered: 0,
    coffeeRefills: 0,
    views: 0,
    bestCombo: 0,
    allTheWay: 0,
    alarms: 0,
    hourlyIndex: [],
    fightsSurvived: 0,
  };
}

export class Game {
  readonly events = new Emitter<GameEvent>();
  readonly options: GameOptions;
  readonly difficulty: Difficulty;
  readonly cook: CookTimes;
  readonly plan: NightPlan;

  /** Gameplay randomness (spawns, orders, events). */
  private readonly rng: Rng;
  /** Cosmetic randomness (lines, looks) — kept separate so flavour never changes gameplay. */
  private readonly flavor: Rng;

  status: 'playing' | 'over' = 'playing';
  /** Simulation seconds (slows down during chair bullet-time). */
  time = 0;
  /** Real seconds since clock-in. */
  realTime = 0;
  /** Shift minutes, 0 = 10:00 PM, 480 = 6:00 AM. */
  minutes = 0;
  index: number = INDEX.max;
  tips = 0;
  combo = 0;
  readonly stats: GameStats = emptyStats();

  readonly seats: Seat[] = [];
  readonly customers = new Map<number, Customer>();
  tickets: Ticket[] = [];
  activeTicketId: number | null = null;
  readonly grill: GrillSlot[];
  readonly irons: WaffleIron[];
  lineJobs: LineJob[] = [];

  fight: FightState | null = null;
  chair: ChairState | null = null;
  storm: StormState | null = null;
  host: HostState | null = null;
  live: LiveState | null = null;
  powerOn = true;
  eggSurcharge = false;
  hostPleased = false;
  femaVerdict: StormState['verdict'] = null;
  /** Name of the hurricane that actually hit (if one did). */
  stormName: string | null = null;
  proposalYes: boolean | null = null;
  selfServed = false;
  sawSir = false;
  raccoonServed = false;
  result: GameResult | null = null;

  private nextSpawnIn = 2.2;
  private lastInputTime = 0;
  private ticketCounter = 0;
  private idCounter = 0;
  private colorCursor = 0;
  private criticalWarned = false;
  private pendingLines: PendingLine[] = [];
  private classicCalled = false;
  private overEmitted = false;
  private readonly pendingSpawnData = new Map<number, SpawnOptions>();

  constructor(options: GameOptions) {
    this.options = options;
    this.difficulty = DIFFICULTIES[options.difficulty];
    this.cook = cookTimes(this.difficulty);
    this.rng = new Rng(options.seed);
    this.flavor = this.rng.fork('flavor');
    this.plan = planNight(this.rng.fork('night'), this.difficulty);
    // The customers you calmed at the shift change come back tonight.
    const comeback = this.rng.fork('returning');
    (options.calm?.returning ?? []).forEach((_, i) => {
      this.plan.events.push({
        id: 'returning',
        at: Math.round(comeback.range(at(22, 25), at(2, 40))),
        fired: false,
        data: i,
      });
    });
    this.plan.events.sort((a, b) => a.at - b.at);

    for (let i = 0; i < STOOLS; i++)
      this.seats.push({ id: i, kind: 'stool', index: i, customerId: null });
    for (let i = 0; i < BOOTHS; i++)
      this.seats.push({ id: STOOLS + i, kind: 'booth', index: i, customerId: null });
    this.grill = Array.from({ length: STATIONS.grillSlots }, (_, id) => ({
      id,
      ticketId: null,
      time: 0,
      toppings: [],
      smokeTime: 0,
      alarmed: false,
    }));
    this.irons = Array.from({ length: STATIONS.waffleIrons }, (_, id) => ({
      id,
      ticketId: null,
      kind: null,
      time: 0,
      smokeTime: 0,
      alarmed: false,
      dinged: false,
    }));
  }

  /* ───────────────────────────── queries ───────────────────────────── */

  get hourIndex(): number {
    return shiftHour(this.minutes);
  }

  get clock(): string {
    return clockLabel(this.minutes);
  }

  get level(): IndexLevel {
    return indexLevel(this.index);
  }

  /** GREEN = full menu. YELLOW or a power outage = the waffle irons are off the menu. */
  get limitedMenu(): boolean {
    return !this.powerOn || this.level !== 'green';
  }

  /** Customers stop losing patience while a fight or the Host has everyone's attention. */
  get patienceFrozen(): boolean {
    return (
      (this.fight !== null && this.fight.phase !== 'ko') ||
      (this.host !== null && this.host.resolved === null)
    );
  }

  get unbothered(): boolean {
    return this.fight !== null && (this.fight.phase === 'round' || this.fight.phase === 'fight');
  }

  get tipMultiplier(): number {
    return (this.unbothered ? TIPS.unbotheredMult : 1) * (this.live ? TIPS.liveMult : 1);
  }

  /** Open tickets, oldest first — the order they hang on the rail. */
  openTickets(): Ticket[] {
    return this.tickets.filter((t) => t.status === 'open');
  }

  ticket(id: number | null): Ticket | undefined {
    if (id === null) return undefined;
    return this.tickets.find((t) => t.id === id && t.status === 'open');
  }

  activeTicket(): Ticket | undefined {
    return this.ticket(this.activeTicketId);
  }

  customer(id: number): Customer | undefined {
    return this.customers.get(id);
  }

  slotFor(ticketId: number): GrillSlot | undefined {
    return this.grill.find((s) => s.ticketId === ticketId);
  }

  ironFor(ticketId: number): WaffleIron | undefined {
    return this.irons.find((i) => i.ticketId === ticketId);
  }

  lineJobFor(ticketId: number, kind: 'eggs' | 'bacon'): LineJob | undefined {
    return this.lineJobs.find((j) => j.ticketId === ticketId && j.kind === kind);
  }

  /** Is this item on a station right now? */
  isCooking(ticket: Ticket, kind: ItemKind): boolean {
    switch (kind) {
      case 'hashBrowns':
        return this.slotFor(ticket.id) !== undefined;
      case 'waffle':
        return this.ironFor(ticket.id) !== undefined;
      case 'eggs':
      case 'bacon':
        return this.lineJobFor(ticket.id, kind) !== undefined;
    }
  }

  isRevealed(ticket: Ticket): boolean {
    return this.time >= ticket.revealAt;
  }

  /* ───────────────────────────── the loop ───────────────────────────── */

  update(realDt: number): void {
    if (this.status !== 'playing') return;
    const step = Math.min(Math.max(realDt, 0), 0.1);
    this.realTime += step;

    let scale = 1;
    if (this.chair) {
      this.updateChair(step);
      if (this.chair) scale = this.chair.outcome ? 0.45 : CHAIR.slowmo;
    }
    const dt = step * scale;
    this.time += dt;

    this.advanceClock(dt);
    this.runDirector();
    this.updateSpawning(dt);
    this.updateCustomers(dt);
    this.updateStations(dt);
    this.updateFight(dt);
    this.updateStorm(dt);
    this.updateHost(dt);
    this.updateLive(dt);
    this.flushLines();
    this.checkIdle();

    if (this.minutes >= SHIFT_MINUTES && !this.chair) this.finish('shiftComplete');
  }

  private advanceClock(dt: number): void {
    const before = this.hourIndex;
    this.minutes = Math.min(
      SHIFT_MINUTES,
      this.minutes + (dt * 60) / this.difficulty.secondsPerHour,
    );
    const hour = this.hourIndex;
    if (hour !== before) {
      this.emit({ type: 'hour', hourIndex: hour, label: this.clock });
    }
    const h = this.stats.hourlyIndex;
    h[hour] = Math.min(h[hour] ?? this.index, this.index);
  }

  /* ───────────────────────────── player actions ───────────────────────────── */

  private touch(): void {
    this.lastInputTime = this.time;
  }

  selectTicket(id: number): ActionResult {
    const t = this.ticket(id);
    if (!t) return fail('That ticket is gone.');
    this.touch();
    if (this.activeTicketId !== id) {
      this.activeTicketId = id;
      this.emit({ type: 'ticketSelected', ticketId: id });
      this.sfx('select');
    }
    return OK;
  }

  /** Select by rail position (keyboard 1–5). */
  selectTicketAt(position: number): ActionResult {
    const t = this.openTickets()[position];
    if (!t) return fail(`No ticket #${position + 1} on the rail.`);
    return this.selectTicket(t.id);
  }

  private requireActive(): Ticket | ActionResult {
    const t = this.activeTicket();
    if (!t) return fail(this.openTickets().length ? 'Tap a ticket first.' : 'No tickets yet.');
    return t;
  }

  dropHashBrowns(slotId?: number): ActionResult {
    const t = this.requireActive();
    if (!('id' in t)) return t;
    if (!t.order.hashBrowns) return fail(`#${t.no} didn't order hash browns.`);
    if (t.plated.hashBrowns) return fail(`#${t.no}'s hash browns are already plated.`);
    if (this.slotFor(t.id)) return fail(`#${t.no}'s hash browns are already on the grill.`);
    const slot =
      slotId === undefined ? this.grill.find((s) => s.ticketId === null) : this.grill[slotId];
    if (!slot || slot.ticketId !== null) return fail('Grill is full!');
    slot.ticketId = t.id;
    slot.time = 0;
    slot.toppings = [];
    slot.smokeTime = 0;
    slot.alarmed = false;
    this.touch();
    this.emit({ type: 'itemStarted', ticketId: t.id, kind: 'hashBrowns' });
    this.sfx('drop');
    return OK;
  }

  toggleTopping(topping: Topping): ActionResult {
    const t = this.requireActive();
    if (!('id' in t)) return t;
    if (!t.order.hashBrowns) return fail(`#${t.no} didn't order hash browns.`);
    if (t.plated.hashBrowns) return fail(`#${t.no}'s hash browns are already plated.`);
    let slot = this.slotFor(t.id);
    if (!slot) {
      const dropped = this.dropHashBrowns();
      if (!dropped.ok) return dropped;
      slot = this.slotFor(t.id);
      if (!slot) return fail('Grill is full!');
    }
    if (hashBrownDoneness(slot.time, this.cook) === 'burnt')
      return fail('Too late. Those are charcoal.');
    this.touch();
    if (slot.toppings.includes(topping)) {
      slot.toppings = slot.toppings.filter((x) => x !== topping);
      this.sfx('scrape');
    } else {
      slot.toppings = [...slot.toppings, topping];
      this.sfx('topping');
    }
    return OK;
  }

  /** Tap a grill slot: empty → drop for the active ticket, burnt → toss, otherwise plate it. */
  tapGrillSlot(slotId: number): ActionResult {
    const slot = this.grill[slotId];
    if (!slot) return fail('No such slot.');
    if (slot.ticketId === null) return this.dropHashBrowns(slotId);
    this.touch();
    const doneness = hashBrownDoneness(slot.time, this.cook);
    const ticket = this.ticket(slot.ticketId);
    if (doneness === 'burnt' || !ticket || !ticket.order.hashBrowns) {
      this.clearSlot(slot);
      this.emit({ type: 'tossed', station: 'grill', id: slotId });
      this.sfx('toss');
      return OK;
    }
    const evaluation = evaluateHashBrowns(ticket.order.hashBrowns, slot.toppings, doneness);
    ticket.plated.hashBrowns = { kind: 'hashBrowns', ...evaluation };
    this.stats.hashBrowns++;
    this.stats.toppings += slot.toppings.length;
    this.clearSlot(slot);
    this.emit({ type: 'itemPlated', ticketId: ticket.id, kind: 'hashBrowns', ...evaluation });
    this.sfx('plate');
    this.checkComplete(ticket);
    return OK;
  }

  tossGrillSlot(slotId: number): ActionResult {
    const slot = this.grill[slotId];
    if (!slot || slot.ticketId === null) return fail('Nothing to toss.');
    this.touch();
    this.clearSlot(slot);
    this.emit({ type: 'tossed', station: 'grill', id: slotId });
    this.sfx('toss');
    return OK;
  }

  pourWaffle(kind: WaffleKind): ActionResult {
    const t = this.requireActive();
    if (!('id' in t)) return t;
    if (!t.order.waffle) return fail(`#${t.no} didn't order a waffle.`);
    if (t.plated.waffle) return fail(`#${t.no}'s waffle is already plated.`);
    if (this.ironFor(t.id)) return fail(`#${t.no}'s waffle is already in the iron.`);
    if (!this.powerOn) return fail("Power's out! The irons are electric.");
    const iron = this.irons.find((i) => i.ticketId === null);
    if (!iron) return fail('Both irons are busy!');
    iron.ticketId = t.id;
    iron.kind = kind;
    iron.time = 0;
    iron.smokeTime = 0;
    iron.alarmed = false;
    iron.dinged = false;
    this.touch();
    this.emit({ type: 'itemStarted', ticketId: t.id, kind: 'waffle' });
    this.sfx('pour');
    return OK;
  }

  tapIron(ironId: number): ActionResult {
    const iron = this.irons[ironId];
    if (!iron) return fail('No such iron.');
    if (iron.ticketId === null || iron.kind === null) {
      const t = this.activeTicket();
      if (t?.order.waffle && !t.plated.waffle && !this.ironFor(t.id)) {
        return fail('Pick a batter: WAFFLE or PECAN.');
      }
      return fail('Iron is empty.');
    }
    this.touch();
    const status = waffleStatus(iron.time, this.cook);
    const ticket = this.ticket(iron.ticketId);
    if (status === 'burnt' || !ticket || !ticket.order.waffle) {
      this.clearIron(iron);
      this.emit({ type: 'tossed', station: 'iron', id: ironId });
      this.sfx('toss');
      return OK;
    }
    if (status === 'cooking') return fail('Not yet — wait for the DING.');
    const evaluation = evaluateWaffle(ticket.order.waffle, iron.kind);
    ticket.plated.waffle = { kind: 'waffle', ...evaluation };
    this.stats.waffles++;
    this.clearIron(iron);
    this.emit({ type: 'itemPlated', ticketId: ticket.id, kind: 'waffle', ...evaluation });
    this.sfx('plate');
    this.checkComplete(ticket);
    return OK;
  }

  tossIron(ironId: number): ActionResult {
    const iron = this.irons[ironId];
    if (!iron || iron.ticketId === null) return fail('Nothing to toss.');
    this.touch();
    this.clearIron(iron);
    this.emit({ type: 'tossed', station: 'iron', id: ironId });
    this.sfx('toss');
    return OK;
  }

  cookEggs(style: EggStyle): ActionResult {
    const t = this.requireActive();
    if (!('id' in t)) return t;
    if (!t.order.eggs) return fail(`#${t.no} didn't order eggs.`);
    if (t.plated.eggs || this.lineJobFor(t.id, 'eggs')) return fail(`#${t.no}'s eggs are handled.`);
    this.lineJobs.push({
      ticketId: t.id,
      kind: 'eggs',
      eggStyle: style,
      time: 0,
      duration: this.cook.eggs,
    });
    this.touch();
    this.emit({ type: 'itemStarted', ticketId: t.id, kind: 'eggs' });
    this.sfx('eggs');
    return OK;
  }

  pullBacon(): ActionResult {
    const t = this.requireActive();
    if (!('id' in t)) return t;
    if (!t.order.bacon) return fail(`#${t.no} didn't order bacon.`);
    if (t.plated.bacon || this.lineJobFor(t.id, 'bacon'))
      return fail(`#${t.no}'s bacon is handled.`);
    this.lineJobs.push({
      ticketId: t.id,
      kind: 'bacon',
      eggStyle: null,
      time: 0,
      duration: this.cook.bacon,
    });
    this.touch();
    this.emit({ type: 'itemStarted', ticketId: t.id, kind: 'bacon' });
    this.sfx('bacon');
    return OK;
  }

  /** Plate whatever is ready for the active ticket (keyboard shortcut). */
  plateActive(): ActionResult {
    const t = this.requireActive();
    if (!('id' in t)) return t;
    const slot = this.slotFor(t.id);
    const iron = this.ironFor(t.id);
    let did = false;
    if (slot) did = this.tapGrillSlot(slot.id).ok || did;
    if (iron && waffleStatus(iron.time, this.cook) !== 'cooking')
      did = this.tapIron(iron.id).ok || did;
    return did ? OK : fail('Nothing on the grill for this ticket.');
  }

  refillCoffee(customerId: number): ActionResult {
    const c = this.customers.get(customerId);
    if (!c || c.state !== 'waiting' || !c.drinksCoffee) return fail('No mug to fill.');
    if (c.mug > 0.85) return fail('Their cup is already full.');
    this.touch();
    const needed = c.mug < FLOW.coffeeLowThreshold;
    c.mug = 1;
    c.patience = Math.min(1, c.patience + (needed ? 0.14 : 0.04));
    this.stats.coffeeRefills++;
    this.emit({ type: 'coffee', customerId, seatId: c.seatId });
    this.sfx('coffee');
    if (needed) {
      const tip = roundCents(this.rng.range(TIPS.coffee[0], TIPS.coffee[1]));
      this.addTips(tip, c.seatId, 'coffee');
    }
    return OK;
  }

  /** Keyboard helper: top off whoever needs it most. */
  refillNeediest(): ActionResult {
    let best: Customer | undefined;
    for (const c of this.customers.values()) {
      if (c.state !== 'waiting' || !c.drinksCoffee || c.mug > 0.85) continue;
      if (!best || c.mug < best.mug) best = c;
    }
    return best ? this.refillCoffee(best.id) : fail('Everybody has coffee.');
  }

  catchChair(): ActionResult {
    const ch = this.chair;
    if (!ch || ch.outcome) return fail('');
    this.touch();
    if (ch.t < ch.grace) return OK;
    if (ch.t < ch.windowStart) {
      this.resolveChair('whiff');
    } else if (Math.abs(ch.t - ch.perfectAt) <= ch.perfectTolerance) {
      this.resolveChair('perfect');
    } else {
      this.resolveChair('caught');
    }
    return OK;
  }

  /* ───────────────────────────── stations ───────────────────────────── */

  private clearSlot(slot: GrillSlot): void {
    slot.ticketId = null;
    slot.time = 0;
    slot.toppings = [];
    slot.smokeTime = 0;
    slot.alarmed = false;
  }

  private clearIron(iron: WaffleIron): void {
    iron.ticketId = null;
    iron.kind = null;
    iron.time = 0;
    iron.smokeTime = 0;
    iron.alarmed = false;
    iron.dinged = false;
  }

  private updateStations(dt: number): void {
    for (const slot of this.grill) {
      if (slot.ticketId === null) continue;
      const before = hashBrownDoneness(slot.time, this.cook);
      slot.time += dt;
      const after = hashBrownDoneness(slot.time, this.cook);
      if (after === 'burnt') {
        if (before !== 'burnt') this.onBurnt('grill', slot.id);
        slot.smokeTime += dt;
        if (slot.smokeTime >= COOK.smokeAlarmAfter && !slot.alarmed) {
          slot.alarmed = true;
          this.onAlarm('grill', slot.id);
          this.clearSlot(slot);
        }
      }
    }

    for (const iron of this.irons) {
      if (iron.ticketId === null) continue;
      if (!this.powerOn) continue;
      const before = waffleStatus(iron.time, this.cook);
      iron.time += dt;
      const after = waffleStatus(iron.time, this.cook);
      if (before === 'cooking' && after === 'ready' && !iron.dinged) {
        iron.dinged = true;
        this.sfx('ding');
      }
      if (after === 'burnt') {
        if (before !== 'burnt') this.onBurnt('iron', iron.id);
        iron.smokeTime += dt;
        if (iron.smokeTime >= COOK.smokeAlarmAfter && !iron.alarmed) {
          iron.alarmed = true;
          this.onAlarm('iron', iron.id);
          this.clearIron(iron);
        }
      }
    }

    const finished: LineJob[] = [];
    for (const job of this.lineJobs) {
      job.time += dt;
      if (job.time >= job.duration) finished.push(job);
    }
    for (const job of finished) {
      this.lineJobs = this.lineJobs.filter((j) => j !== job);
      const ticket = this.ticket(job.ticketId);
      if (!ticket) continue;
      if (job.kind === 'eggs' && ticket.order.eggs && job.eggStyle) {
        const evaluation = evaluateEggs(ticket.order.eggs, job.eggStyle);
        ticket.plated.eggs = { kind: 'eggs', ...evaluation };
        this.emit({ type: 'itemPlated', ticketId: ticket.id, kind: 'eggs', ...evaluation });
      } else if (job.kind === 'bacon') {
        ticket.plated.bacon = { kind: 'bacon', quality: 1, issues: [] };
        this.emit({
          type: 'itemPlated',
          ticketId: ticket.id,
          kind: 'bacon',
          quality: 1,
          issues: [],
        });
      }
      this.sfx('plate');
      this.checkComplete(ticket);
    }
  }

  private onBurnt(station: 'grill' | 'iron', id: number): void {
    this.stats.burnt++;
    this.combo = 0;
    this.emit({ type: 'burnt', station, id });
    this.sfx('burn');
    this.changeIndex(INDEX.burnt);
  }

  private onAlarm(station: 'grill' | 'iron', id: number): void {
    this.stats.alarms++;
    this.emit({ type: 'alarm', station, id });
    this.sfx('alarm');
    this.changeIndex(INDEX.alarm);
    this.say(
      'dottie',
      this.flavor.pick([
        'I got it, baby. Focus.',
        'Smoke alarm again?! Lord.',
        "I'll toss it. You COOK.",
      ]),
      2.4,
    );
  }

  /* ───────────────────────────── tickets & service ───────────────────────────── */

  private checkComplete(ticket: Ticket): void {
    if (ticket.status === 'open' && isTicketComplete(ticket)) this.serve(ticket);
  }

  private serve(ticket: Ticket): void {
    const customer = this.customers.get(ticket.customerId);
    ticket.status = 'served';
    const quality = ticketQuality(ticket);
    const perfect = isPerfect(ticket);
    this.combo = perfect ? this.combo + 1 : 0;
    this.stats.bestCombo = Math.max(this.stats.bestCombo, this.combo);
    this.stats.served++;
    if (perfect) this.stats.perfect++;
    if (quality < 0.8) this.stats.wrong++;
    if (perfect && ticket.order.hashBrowns && isAllTheWay(ticket.order.hashBrowns.toppings))
      this.stats.allTheWay++;

    const multiplier = this.tipMultiplier;
    if (this.unbothered && this.fight) {
      this.stats.unbothered++;
      this.fight.servedDuring++;
    }
    const subtotal = orderSubtotal(ticket.order, this.eggSurcharge);
    const tip = computeTip({
      subtotal,
      quality,
      patience: customer?.patience ?? 0.5,
      tipMult: customer?.tipMult ?? 1,
      multiplier,
      combo: this.combo,
    });
    this.addTips(tip, ticket.seatId, this.unbothered ? 'unbothered' : perfect ? 'perfect' : 'tip');
    this.changeIndex(indexDeltaForService(quality));
    this.emit({
      type: 'ticketServed',
      ticket,
      quality,
      perfect,
      tip,
      combo: this.combo,
      multiplier,
    });
    this.sfx(perfect ? 'perfect' : 'orderUp');

    if (customer) {
      customer.state = 'eating';
      customer.stateTime = 0;
      if (customer.archetype === 'raccoon') this.raccoonServed = true;
      if (customer.archetype !== 'host') this.reactToService(customer, ticket, perfect);
    }
    if (ticket.tags.includes('proposal')) this.resolveProposal(ticket, perfect);
    if (ticket.tags.includes('host')) this.resolveHost(perfect ? 'pleased' : 'tolerant');
    if (perfect && this.combo > 0 && this.combo % 5 === 0) {
      this.say(
        'dottie',
        this.flavor.pick(['Look at you GO!', "That's my cook!", 'Somebody call the news.']),
        2,
      );
    }
    this.removeTicket(ticket);
  }

  private reactToService(customer: Customer, ticket: Ticket, perfect: boolean): void {
    const arch = ARCHETYPES[customer.archetype];
    if (perfect) {
      const pool = arch.lines.happy.length ? arch.lines.happy : GENERIC_HAPPY;
      this.say(customer.id, this.flavor.pick(pool), 2.4, 'happy');
      return;
    }
    const issue = ticketIssues(ticket)[0];
    const pool = arch.lines.wrong.length ? arch.lines.wrong : GENERIC_WRONG;
    const line = this.flavor.pick(pool);
    const text = issue && customer.archetype !== 'raccoon' ? `${capitalize(issue)}. ${line}` : line;
    this.say(customer.id, text, 2.8, 'angry');
  }

  private removeTicket(ticket: Ticket): void {
    this.tickets = this.tickets.filter((t) => t !== ticket);
    for (const slot of this.grill) if (slot.ticketId === ticket.id) this.clearSlot(slot);
    for (const iron of this.irons) if (iron.ticketId === ticket.id) this.clearIron(iron);
    this.lineJobs = this.lineJobs.filter((j) => j.ticketId !== ticket.id);
    if (this.activeTicketId === ticket.id) {
      const next = this.openTickets()[0];
      this.activeTicketId = next ? next.id : null;
      this.emit({ type: 'ticketSelected', ticketId: this.activeTicketId });
    }
  }

  private cancelTicket(ticket: Ticket, reason: 'walkout' | 'fight' | 'selfServe' | 'host'): void {
    ticket.status = 'cancelled';
    this.emit({ type: 'ticketCancelled', ticket, reason });
    this.removeTicket(ticket);
  }

  private createTicket(customer: Customer, order: OrderSpec, tags: TicketTag[]): Ticket {
    this.ticketCounter++;
    const ticket: Ticket = {
      id: this.ticketCounter,
      no: this.ticketCounter,
      seatId: customer.seatId,
      customerId: customer.id,
      order,
      plated: {},
      createdAt: this.time,
      color: this.colorCursor++ % 6,
      tags,
      revealAt: tags.includes('usual') ? this.time + FLOW.usualReveal : this.time,
      status: 'open',
    };
    this.tickets.push(ticket);
    customer.ticketId = ticket.id;
    const firstHourBonus = this.hourIndex === 0 ? FLOW.firstHourPatienceBonus : 1;
    const tutorialBonus = this.options.firstShift && this.minutes < 90 ? 1.35 : 1;
    const calmBonus = 1 + (this.options.calm?.patienceBonus ?? 0);
    customer.patienceSeconds =
      (FLOW.patienceBase + FLOW.patiencePerItem * itemCount(order)) *
      ARCHETYPES[customer.archetype].patience *
      this.difficulty.patienceMult *
      firstHourBonus *
      tutorialBonus *
      calmBonus;
    customer.patience = 1;
    if (this.activeTicketId === null || !this.activeTicket()) {
      this.activeTicketId = ticket.id;
      this.emit({ type: 'ticketSelected', ticketId: ticket.id });
    }
    this.emit({ type: 'ticketCreated', ticket, call: orderCall(order), speak: orderSpeech(order) });
    return ticket;
  }

  private addTips(amount: number, seatId: number, label: string): void {
    const value = roundCents(amount);
    this.tips = roundCents(this.tips + value);
    this.emit({ type: 'tip', amount: value, seatId, label });
    this.sfx('cash');
  }

  /* ───────────────────────────── the Waffle House Index ───────────────────────────── */

  changeIndex(delta: number): void {
    if (this.status !== 'playing' || delta === 0) return;
    const adjusted = delta < 0 ? delta * this.difficulty.indexLossMult : delta;
    const previous = this.index;
    const previousLevel = indexLevel(previous);
    this.index = clamp(Math.round((previous + adjusted) * 10) / 10, 0, INDEX.max);
    if (this.index === previous) return;
    const level = indexLevel(this.index);
    this.emit({
      type: 'indexChanged',
      value: this.index,
      delta: this.index - previous,
      level,
      previousLevel,
    });
    const h = this.stats.hourlyIndex;
    h[this.hourIndex] = Math.min(h[this.hourIndex] ?? this.index, this.index);

    if (level !== previousLevel) {
      if (level === 'yellow' && previousLevel === 'green') {
        this.banner('INDEX: YELLOW', 'LIMITED MENU — waffle irons are off the menu', 'danger', 2.6);
        this.headline('FEMA: LOCAL WAFFLE HOUSE INDEX DOWNGRADED TO YELLOW', true);
        this.say('dottie', "Baby, FEMA's gonna start askin' questions.", 2.6);
      } else if (level === 'green' && previousLevel === 'yellow') {
        this.banner('INDEX: GREEN', 'Full menu restored. FEMA relaxes.', 'good', 2.2);
        this.headline('WAFFLE HOUSE INDEX BACK TO GREEN; NATION EXHALES');
      }
    }
    if (this.index > 0 && this.index < INDEX.criticalAt && !this.criticalWarned) {
      this.criticalWarned = true;
      this.say(
        'dottie',
        'We are ONE bad night from CLOSED. If we close, the whole South panics.',
        3.2,
      );
    }
    if (this.index >= INDEX.greenAt) this.criticalWarned = false;
    if (this.index <= 0) this.finish('closed');
  }

  /* ───────────────────────────── customers ───────────────────────────── */

  private ticketCap(): number {
    const profile = HOURS[this.hourIndex] ?? HOURS[0]!;
    if (this.options.firstShift && this.minutes < 30) return 1;
    return clamp(profile.maxTickets + this.difficulty.maxTicketsBonus, 1, STATIONS.maxTicketsHard);
  }

  private updateSpawning(dt: number): void {
    if (this.minutes >= at(5, 50)) return;
    if (this.host && this.host.resolved === null) return;
    this.nextSpawnIn -= dt;
    if (this.nextSpawnIn > 0) return;
    const profile = HOURS[this.hourIndex] ?? HOURS[0]!;
    const tutorial = this.options.firstShift && this.minutes < 60 ? 1.3 : 1;
    this.nextSpawnIn =
      (profile.spawnEvery / this.difficulty.spawnMult) * tutorial * this.rng.range(0.7, 1.3);
    if (this.openTickets().length >= this.ticketCap()) {
      this.nextSpawnIn = Math.min(this.nextSpawnIn, 1.2);
      return;
    }
    this.spawnRandom();
  }

  private spawnRandom(): boolean {
    const profile = HOURS[this.hourIndex] ?? HOURS[0]!;
    const exclude = new Set<ArchetypeId>();
    for (const c of this.customers.values()) if (c.archetype === 'raccoon') exclude.add('raccoon');
    const arch = pickArchetype(this.rng, this.hourIndex, exclude);
    const coffeeOnly = this.rng.chance(Math.max(profile.coffeeOnly, arch.coffeeOnly));
    // The very first ticket of every shift is the classic call.
    const first = !this.classicCalled && !coffeeOnly;
    const spawned = this.spawn(arch, {
      coffeeOnly,
      seatKind: coffeeOnly ? 'stool' : arch.seat,
      ...(first
        ? { order: { hashBrowns: { toppings: ['smothered', 'covered'], well: false } } }
        : {}),
    });
    if (spawned && first) this.classicCalled = true;
    return spawned !== null;
  }

  private freeSeat(kind: 'stool' | 'booth' | 'any'): Seat | undefined {
    const pool = this.seats.filter(
      (s) => s.customerId === null && (kind === 'any' || s.kind === kind),
    );
    return pool.length ? this.rng.pick(pool) : undefined;
  }

  private spawn(arch: Archetype, opts: SpawnOptions = {}): Customer | null {
    let seatId = HOST_SEAT_ID;
    if (!opts.virtualSeat) {
      const want = opts.seatKind ?? arch.seat;
      // Coffee drinkers need a stool (mugs live on the counter); parties need a booth.
      const strict = opts.coffeeOnly === true || arch.seat === 'booth';
      const seat = this.freeSeat(want) ?? (strict ? undefined : this.freeSeat('any'));
      if (!seat) return null;
      seat.customerId = ++this.idCounter;
      seatId = seat.id;
    } else {
      ++this.idCounter;
    }
    const seat = this.seats.find((s) => s.id === seatId);
    const human = arch.id !== 'host';
    const customer: Customer = {
      id: this.idCounter,
      archetype: arch.id,
      name: opts.name ?? arch.name,
      seatId,
      look: opts.look ?? arch.look(this.flavor),
      state: 'entering',
      stateTime: 0,
      patience: 1,
      patienceSeconds: 60,
      mug: 1,
      drinksCoffee: human && seat?.kind === 'stool',
      coffeeOnly: opts.coffeeOnly ?? false,
      ticketId: null,
      tipMult: opts.tipMult ?? arch.tip,
      angry: false,
      coffeeVisitLeft: this.rng.range(FLOW.coffeeOnlyVisit[0], FLOW.coffeeOnlyVisit[1]),
    };
    this.customers.set(customer.id, customer);
    this.pendingSpawnData.set(customer.id, opts);
    this.emit({ type: 'customerArrived', customer });
    if (!opts.virtualSeat) this.sfx('door');
    return customer;
  }

  private onSeated(c: Customer): void {
    const opts = this.pendingSpawnData.get(c.id) ?? {};
    this.pendingSpawnData.delete(c.id);
    const arch = ARCHETYPES[c.archetype];
    c.state = 'waiting';
    c.stateTime = 0;
    const line =
      opts.arriveLine ?? (arch.lines.arrive.length ? this.flavor.pick(arch.lines.arrive) : null);
    if (line)
      this.say(
        c.id,
        line,
        c.archetype === 'philosopher' ? 3.4 : 2.6,
        c.archetype === 'host' ? 'spooky' : 'normal',
      );

    if (c.coffeeOnly) {
      c.patienceSeconds = 999;
      c.mug = this.rng.range(0.15, 0.4);
      return;
    }
    const order =
      opts.order ??
      generateOrder(this.rng, {
        hour: this.hourIndex,
        limitedMenu: this.limitedMenu,
        ...(arch.orderBias ? { bias: arch.orderBias } : {}),
      });
    const safeOrder = this.limitedMenu && order.waffle ? withoutWaffle(order) : order;
    this.createTicket(c, safeOrder, opts.tags ?? []);
    if (opts.patienceMult) c.patienceSeconds *= opts.patienceMult;

    if (c.archetype === 'philosopher') {
      this.say('dottie', 'Sir, this is a Waffle House.', 2.6, 'normal', 3.2);
      this.sawSir = true;
    }
    if (c.archetype === 'tourist' && line?.includes("Denny's")) {
      this.say('crowd', '*entire restaurant gasps*', 2, 'normal', 1.4);
    }
    if (c.archetype === 'streamer') this.startLive(c);
  }

  private updateCustomers(dt: number): void {
    const frozen = this.patienceFrozen;
    for (const c of [...this.customers.values()]) {
      c.stateTime += dt;
      switch (c.state) {
        case 'entering':
          if (c.stateTime >= FLOW.walkIn + FLOW.settle) this.onSeated(c);
          break;
        case 'waiting':
          this.updateWaiting(c, dt, frozen);
          break;
        case 'eating':
          if (c.stateTime >= FLOW.eat) {
            c.state = 'leaving';
            c.stateTime = 0;
          }
          break;
        case 'leaving':
          if (c.stateTime >= FLOW.leave) this.removeCustomer(c);
          break;
        case 'gone':
          break;
      }
    }
  }

  private updateWaiting(c: Customer, dt: number, frozen: boolean): void {
    if (c.archetype === 'host') return;
    if (c.drinksCoffee && !frozen) c.mug = Math.max(0, c.mug - dt * FLOW.coffeeDrainPerSecond);

    if (c.coffeeOnly) {
      if (c.archetype === 'fema') return; // FEMA stays until the storm has passed
      if (!frozen) c.coffeeVisitLeft -= dt;
      if (c.mug <= 0 && c.stateTime > 6) {
        // Nobody refilled it. They shrug and leave — no harm, no tip.
        this.say(
          c.id,
          this.flavor.pick([
            '*shakes empty mug*',
            "Guess I'll go.",
            'Coffee was good while it lasted.',
          ]),
          2,
        );
        this.leave(c, false);
      } else if (c.coffeeVisitLeft <= 0) {
        this.leave(c, false);
      }
      return;
    }

    if (frozen || c.ticketId === null) return;
    const drain =
      (c.drinksCoffee && c.mug <= 0 ? FLOW.coffeeEmptyPatienceMult : 1) / c.patienceSeconds;
    const before = c.patience;
    c.patience = Math.max(0, c.patience - dt * drain);
    if (before >= 0.3 && c.patience < 0.3) {
      this.say(c.id, this.flavor.pick(GENERIC_WAITING), 1.8, 'angry');
    }
    if (c.patience <= 0) this.walkout(c);
  }

  private walkout(c: Customer): void {
    const ticket = this.ticket(c.ticketId);
    const arch = ARCHETYPES[c.archetype];
    this.stats.walkouts++;
    this.combo = 0;
    c.angry = true;
    this.say(
      c.id,
      this.flavor.pick(arch.lines.angry.length ? arch.lines.angry : GENERIC_ANGRY),
      2.6,
      'angry',
    );
    this.sfx('walkout');
    if (ticket) {
      if (ticket.tags.includes('proposal')) this.proposalYes = false;
      this.cancelTicket(ticket, 'walkout');
    }
    this.leave(c, true);
    this.changeIndex(INDEX.walkout);
  }

  private leave(c: Customer, angry: boolean): void {
    c.state = 'leaving';
    c.stateTime = 0;
    c.angry = angry;
    this.emit({ type: 'customerLeft', customer: c, angry });
  }

  private removeCustomer(c: Customer): void {
    c.state = 'gone';
    this.customers.delete(c.id);
    const seat = this.seats.find((s) => s.customerId === c.id);
    if (seat) seat.customerId = null;
    if (this.live && this.live.customerId === c.id) this.endLive();
  }

  /** A random seated customer (for crowd reactions and copypasta). */
  private randomSeated(exclude?: number): Customer | undefined {
    const pool = [...this.customers.values()].filter(
      (c) =>
        c.state === 'waiting' &&
        c.id !== exclude &&
        c.seatId !== HOST_SEAT_ID &&
        c.archetype !== 'raccoon',
    );
    return pool.length ? this.flavor.pick(pool) : undefined;
  }

  /* ───────────────────────────── the night's script ───────────────────────────── */

  private runDirector(): void {
    for (const ev of this.plan.events) {
      if (ev.fired || this.minutes < ev.at) continue;
      if (this.chair) return; // don't stack set-pieces on top of a flying chair
      ev.fired = this.fire(ev);
      if (!ev.fired) {
        // Couldn't happen yet (no free seat, a fight in progress…) — try again shortly.
        ev.at += 4;
        if (ev.at > SHIFT_MINUTES - 10) ev.fired = true;
      }
    }
  }

  private fire(ev: ScheduledEvent): boolean {
    switch (ev.id) {
      case 'openingLine':
        this.say(
          'dottie',
          this.flavor.pick([
            "Keep the tickets movin' and that Index GREEN, baby.",
            "Doors don't lock. We never close. Let's cook.",
            "FEMA's watchin'. Don't embarrass me.",
          ]),
          3,
        );
        return true;
      case 'regular':
        return this.spawnScripted('regular', {
          order: {
            hashBrowns: { toppings: ['smothered', 'covered', 'peppered'], well: true },
            bacon: true,
          },
          tags: ['usual'],
        });
      case 'proposal': {
        const ok = this.spawnScripted('couple', {
          seatKind: 'booth',
          order: {
            hashBrowns: { toppings: ['smothered', 'covered'], well: false },
            waffle: 'pecan',
          },
          tags: ['proposal'],
          patienceMult: 1.3,
        });
        if (ok) {
          this.say(
            'dottie',
            "Booth's about to propose. Do NOT mess this up, baby.",
            3,
            'normal',
            2.6,
          );
          this.headline(
            'LOCAL COUPLE CHOOSES WAFFLE HOUSE FOR PROPOSAL; "WHERE ELSE," SAYS PARTNER',
          );
        }
        return ok;
      }
      case 'eggSurcharge':
        this.eggSurcharge = true;
        this.headline('BREAKING: EGG PRICES SURGE — 50¢ SURCHARGE ADDED PER EGG', true);
        this.say('dottie', 'Eggs cost more now. Somebody is gonna have FEELINGS about it.', 3);
        this.spawnScripted('eggKaren', {
          order: { eggs: 'scrambled', hashBrowns: { toppings: ['covered'], well: false } },
        });
        return true;
      case 'sirThisIsAWaffleHouse':
        return this.spawnScripted('philosopher', {});
      case 'streamer':
        return this.spawnScripted('streamer', {});
      case 'clubLetOut':
        this.banner('THE CLUB JUST LET OUT', 'Brace yourself.', 'danger', 2.4);
        this.headline('ALERT: AREA CLUB HAS LET OUT. WAFFLE HOUSES ON HIGH ALERT', true);
        this.say('dottie', 'Lord, here they come.', 2.2, 'normal', 1);
        this.spawnScripted('club', {});
        this.nextSpawnIn = 1.2;
        return true;
      case 'fight':
        if (this.fight || this.host?.resolved === null) return false;
        this.startFight();
        return true;
      case 'witchingHour':
        this.banner("IT'S 3 AM.", 'Anything can happen.', 'info', 2.6);
        this.headline('BREAKING: IT IS NOW 3AM. EXPERTS URGE CAUTION, HASH BROWNS', true);
        this.say('dottie', 'Here we go.', 2);
        return true;
      case 'host':
        if (this.fight) return false;
        this.startHost();
        return true;
      case 'weatherman': {
        const ok = this.spawnScripted('weatherman', {});
        if (ok) {
          this.headline(
            'BREAKING: WEATHERMAN SPOTTED AT LOCAL WAFFLE HOUSE. RESIDENTS URGED TO PANIC',
            true,
          );
          const witness = this.randomSeated();
          if (witness) this.say(witness.id, "Oh no. If HE'S here...", 2.4, 'normal', 2.8);
        }
        return ok;
      }
      case 'hurricane':
        if (this.fight) return false;
        this.startStorm();
        return true;
      case 'raccoonHour': {
        const ok = this.spawnScripted('raccoon', {});
        if (ok) {
          const witness = this.randomSeated();
          if (witness) this.say(witness.id, 'Is that... a raccoon?', 2, 'normal', 2.2);
          this.say('dottie', "He's a regular. Leave him be.", 2.4, 'normal', 4.2);
        }
        return ok;
      }
      case 'newHostSpam': {
        const who = this.randomSeated();
        if (!who) return false;
        this.say(who.id, NEW_HOST_LINE, 3, 'spooky');
        const other = this.randomSeated(who.id);
        if (other)
          this.say(
            other.id,
            this.flavor.pick(['...Real.', '*nods solemnly*', 'It really has.']),
            2,
            'normal',
            3.2,
          );
        this.headline('THE WAFFLE HOUSE HAS FOUND ITS NEW HOST');
        return true;
      }
      case 'jukebox': {
        const song = this.flavor.pick(JUKEBOX_SONGS);
        this.emit({ type: 'jukebox', song });
        const who = this.randomSeated();
        if (who)
          this.say(
            who.id,
            this.flavor.pick([
              'Ooh, this is my JAM.',
              'Not THIS song again.',
              '*sings along, badly*',
              'WHO PUT THIS ON',
            ]),
            2.2,
          );
        return true;
      }
      case 'churchLadies':
        this.say('dottie', 'Church ladies. Look alive.', 2.2);
        this.spawnScripted('churchLady', { seatKind: 'booth' });
        return true;
      case 'lastCall':
        this.say('dottie', "Almost there, baby. Day shift's pullin' in.", 2.6);
        return true;
      case 'returning': {
        const who = this.options.calm?.returning[ev.data ?? 0];
        if (!who) return true;
        const ok = this.spawnScripted('returning', {
          look: who.look,
          name: who.name,
          arriveLine: who.line,
          tags: ['calmed'],
        });
        if (ok)
          this.say(
            'dottie',
            this.flavor.pick([
              'Look who’s back. Behavin’, too.',
              'Ain’t that the one you calmed down?',
              'See? Once they calm down, they come back.',
            ]),
            2.4,
            'normal',
            2.2,
          );
        return ok;
      }
    }
  }

  private spawnScripted(id: ArchetypeId, opts: SpawnOptions): boolean {
    return this.spawn(ARCHETYPES[id], opts) !== null;
  }

  /* ───────────────────────────── fights & flying chairs ───────────────────────────── */

  private startFight(): void {
    const topic = this.rng.pick(FIGHT_TOPICS);
    const names = this.rng.shuffle([...FIGHTER_NAMES]).slice(0, 2) as [string, string];
    const [minChairs, maxChairs] = this.difficulty.chairsPerFight;
    const chairs = this.rng.int(minChairs, maxChairs);
    const fightDuration = this.rng.range(12, 15) + chairs * 1.2;
    const chairTimes: number[] = [];
    for (let i = 0; i < chairs; i++) {
      const slot = (fightDuration - 3) / chairs;
      chairTimes.push(2.4 + slot * i + this.rng.range(0, slot * 0.5));
    }
    this.fight = {
      phase: 'argue',
      t: 0,
      topic: topic.topic,
      fighters: names,
      looks: [ARCHETYPES.fighter.look(this.flavor), ARCHETYPES.fighter.look(this.flavor)],
      hp: [1, 1],
      chairTimes,
      chairsThrown: 0,
      fightDuration,
      servedDuring: 0,
      flawless: false,
      round: 1,
    };
    this.emit({ type: 'fightPhase', phase: 'argue', fight: this.fight });
    this.say('fighterA', topic.lines[0], 1.6, 'angry');
    this.say('fighterB', topic.lines[1], 1.6, 'angry', 1.5);
    this.say('fighterA', topic.lines[2], 1.4, 'angry', 3);
  }

  private updateFight(dt: number): void {
    const f = this.fight;
    if (!f) return;
    f.t += dt;
    switch (f.phase) {
      case 'argue':
        if (f.t >= 4.4) this.setFightPhase('round');
        break;
      case 'round':
        if (f.t >= 1.7) this.setFightPhase('fight');
        break;
      case 'fight': {
        const damage = dt * 0.055;
        f.hp[0] = Math.max(0.05, f.hp[0] - damage * this.flavor.range(0.4, 1.6));
        f.hp[1] = Math.max(0.05, f.hp[1] - damage * this.flavor.range(0.4, 1.6));
        const nextChair = f.chairTimes[f.chairsThrown];
        if (nextChair !== undefined && f.t >= nextChair && !this.chair) {
          f.chairsThrown++;
          this.throwChair();
        }
        const done = f.chairsThrown >= f.chairTimes.length && f.t >= f.fightDuration;
        if (done && !this.chair) this.setFightPhase('ko');
        break;
      }
      case 'ko':
        if (f.t >= 2.6) {
          this.stats.fightsSurvived++;
          this.emit({ type: 'fightEnded', fight: f });
          this.fight = null;
        }
        break;
    }
  }

  private setFightPhase(phase: FightState['phase']): void {
    const f = this.fight;
    if (!f) return;
    f.phase = phase;
    f.t = 0;
    this.emit({ type: 'fightPhase', phase, fight: f });
    if (phase === 'round') {
      this.banner(`ROUND ${f.round}`, 'FIGHT!', 'fight', 1.7);
      this.sfx('fightBell');
      this.say(
        'dottie',
        this.flavor.pick(['Not again...', 'Not in MY Waffle House.', 'Every. Single. Night.']),
        2,
      );
    } else if (phase === 'fight') {
      this.headline(`BRAWL ERUPTS AT LOCAL WAFFLE HOUSE OVER "${f.topic}"`, true);
    } else if (phase === 'ko') {
      if (f.flawless) {
        this.banner('FLAWLESS', 'The chair has been caught. The fight is over.', 'ko', 2.4);
      } else {
        const loser = f.hp[0] < f.hp[1] ? 0 : 1;
        f.hp[loser] = 0;
        this.banner('K.O.!', `${f.fighters[1 - loser]} WINS. NOBODY WINS.`, 'ko', 2.4);
      }
      this.sfx('bell');
      this.say('dottie', "Y'ALL TAKE THAT MESS OUTSIDE.", 2.4, 'angry', 0.8);
    }
  }

  private throwChair(): void {
    const flight = CHAIR.flight * this.difficulty.chairFlightMult * this.rng.range(0.92, 1.08);
    const window = CHAIR.window * this.difficulty.chairWindowMult;
    const tolerance = CHAIR.perfectTolerance * this.difficulty.chairWindowMult;
    const perfectAt =
      flight - Math.min(window * 0.6, CHAIR.perfectBeforeImpact * this.difficulty.chairWindowMult);
    this.chair = {
      t: 0,
      duration: flight,
      windowStart: flight - window,
      perfectAt,
      perfectTolerance: tolerance,
      grace: CHAIR.grace,
      targetX: this.rng.range(70, 186),
      spin: this.rng.chance(0.5) ? 1 : -1,
      outcome: null,
      resolvedT: 0,
    };
    this.stats.chairsThrown++;
    this.emit({ type: 'chairThrown', chair: this.chair });
    this.sfx('whoosh');
  }

  private updateChair(dt: number): void {
    const ch = this.chair;
    if (!ch) return;
    if (ch.outcome === null) {
      ch.t += dt;
      if (ch.t >= ch.duration) this.resolveChair('hit');
    } else {
      ch.resolvedT += dt;
      if (ch.resolvedT >= CHAIR.resolveHold) this.chair = null;
    }
  }

  private resolveChair(outcome: ChairOutcome): void {
    const ch = this.chair;
    if (!ch || ch.outcome) return;
    ch.outcome = outcome;
    ch.resolvedT = 0;
    let collateral: string | null = null;
    if (outcome === 'caught' || outcome === 'perfect') {
      this.stats.chairsCaught++;
      if (outcome === 'perfect') this.stats.perfectCatches++;
      this.sfx('catch');
      this.sfx('crowdOoh');
      this.addTips(outcome === 'perfect' ? TIPS.perfectCatch : TIPS.chairCatch, -1, 'hazard pay');
      if (this.live) {
        const clip = Math.round(this.rng.range(1.2, 3.4) * 1_000_000);
        this.stats.views += clip;
        this.banner(
          'CLIPPED!',
          `${(clip / 1_000_000).toFixed(1)}M views and climbing`,
          'good',
          1.8,
        );
      }
      this.say(
        'dottie',
        this.flavor.pick([
          '...Did you just CATCH that?',
          "Baby, you're gonna be on the news.",
          "Put it down gently. We ain't animals.",
        ]),
        2.4,
        'normal',
        0.6,
      );
      if (outcome === 'perfect' && this.fight && this.fight.phase === 'fight') {
        this.fight.flawless = true;
        this.fight.chairsThrown = this.fight.chairTimes.length;
        this.fight.fightDuration = Math.min(this.fight.fightDuration, this.fight.t);
      }
    } else {
      this.sfx('crash');
      collateral = this.chairCollateral();
      this.changeIndex(INDEX.chairHit);
      this.say(
        'dottie',
        this.flavor.pick([
          "Everybody okay? ...The plates aren't.",
          'THAT was a brand new napkin dispenser.',
          'Somebody grab the broom.',
        ]),
        2.4,
        'normal',
        0.6,
      );
    }
    this.emit({ type: 'chairResolved', outcome, collateral });
  }

  private chairCollateral(): string {
    type Target = { label: string; apply: () => void };
    const targets: Target[] = [];
    for (const t of this.openTickets()) {
      if (t.tags.includes('host')) continue;
      for (const kind of ['hashBrowns', 'waffle', 'eggs', 'bacon'] as const) {
        if (t.plated[kind]) {
          targets.push({
            label: `The chair took out #${t.no}'s ${ITEM_LABEL[kind].toLowerCase()}.`,
            apply: () => {
              delete t.plated[kind];
            },
          });
        }
      }
    }
    for (const slot of this.grill) {
      const t = this.ticket(slot.ticketId);
      if (t)
        targets.push({
          label: `The chair knocked #${t.no}'s hash browns off the grill.`,
          apply: () => this.clearSlot(slot),
        });
    }
    if (targets.length === 0) return "CRASH! Everyone's fine. The napkin dispenser is not.";
    const target = this.rng.pick(targets);
    target.apply();
    return `CRASH! ${target.label}`;
  }

  /* ───────────────────────────── hurricane ───────────────────────────── */

  private startStorm(): void {
    const duration = this.difficulty.secondsPerHour;
    const name = this.plan.hurricaneName;
    this.stormName = name;
    this.storm = {
      name,
      t: 0,
      duration,
      outages: [
        { at: duration * 0.22, length: this.rng.range(8, 10) },
        { at: duration * 0.62, length: this.rng.range(8, 10) },
      ],
      minIndex: this.index,
      verdict: null,
      femaCustomerId: null,
      nextThunder: 2,
    };
    this.emit({ type: 'storm', active: true, name });
    this.banner(`HURRICANE ${name}`, 'FEMA is watching the Waffle House Index.', 'storm', 2.8);
    this.headline(
      `HURRICANE ${name} MAKES LANDFALL. FEMA MONITORING LOCAL WAFFLE HOUSE INDEX`,
      true,
    );
    this.emit({ type: 'jukebox', song: 'Hurricane Two-Step' });
    const fema = this.spawn(ARCHETYPES.fema, { coffeeOnly: true, seatKind: 'stool' });
    if (fema) this.storm.femaCustomerId = fema.id;
  }

  private updateStorm(dt: number): void {
    const s = this.storm;
    if (!s) return;
    s.t += dt;
    s.minIndex = Math.min(s.minIndex, this.index);

    const outageNow = s.outages.some((o) => s.t >= o.at && s.t < o.at + o.length);
    if (outageNow && this.powerOn) {
      this.powerOn = false;
      this.emit({ type: 'power', on: false });
      this.sfx('powerDown');
      this.say('dottie', "Grill's on gas, baby. KEEP COOKIN'.", 2.4, 'normal', 0.8);
    } else if (!outageNow && !this.powerOn) {
      this.powerOn = true;
      this.emit({ type: 'power', on: true });
      this.sfx('powerUp');
    }

    s.nextThunder -= dt;
    if (s.nextThunder <= 0) {
      s.nextThunder = this.flavor.range(5, 11);
      this.sfx('thunder');
    }

    if (s.t >= s.duration) this.endStorm();
  }

  private endStorm(): void {
    const s = this.storm;
    if (!s) return;
    if (!this.powerOn) {
      this.powerOn = true;
      this.emit({ type: 'power', on: true });
    }
    const verdict =
      indexLevel(s.minIndex) === 'green' ? 'green' : this.index > 0 ? 'yellow' : 'red';
    s.verdict = verdict;
    this.femaVerdict = verdict;
    if (verdict === 'green') {
      this.banner(
        'FEMA VERDICT: GREEN',
        '"If this Waffle House is open, we\'re gonna be fine."',
        'good',
        3,
      );
      this.headline(`FEMA: WAFFLE HOUSE INDEX STAYED GREEN THROUGH HURRICANE ${s.name}`, true);
      this.addTips(TIPS.femaGreen, -1, 'FEMA bonus');
    } else {
      this.banner(
        'FEMA VERDICT: YELLOW',
        'Resources are being sent. To you. Specifically.',
        'danger',
        3,
      );
      this.headline(`HURRICANE ${s.name} PASSES; LOCAL WAFFLE HOUSE RATED "YELLOW" BY FEMA`);
    }
    const fema = s.femaCustomerId !== null ? this.customers.get(s.femaCustomerId) : undefined;
    if (fema && fema.state === 'waiting') {
      this.say(
        fema.id,
        verdict === 'green'
          ? '*checks a box marked GREEN*'
          : '*writes YELLOW, underlines it twice*',
        2.6,
      );
      this.leave(fema, false);
    }
    this.emit({ type: 'storm', active: false, name: s.name });
    this.storm = null;
  }

  /* ───────────────────────────── 3:33 AM ───────────────────────────── */

  private startHost(): void {
    if (this.index < INDEX.hostRequires) {
      this.emit({ type: 'sfx', name: 'glitch' });
      return;
    }
    const host = this.spawn(ARCHETYPES.host, { virtualSeat: true });
    if (!host) return;
    host.state = 'waiting';
    host.stateTime = 0;
    this.pendingSpawnData.delete(host.id);
    this.say(host.id, '...ALL. THE. WAY...', 3, 'spooky');
    const ticket = this.createTicket(
      host,
      { hashBrowns: { toppings: [...TOPPINGS], well: false } },
      ['host'],
    );
    this.activeTicketId = ticket.id;
    this.emit({ type: 'ticketSelected', ticketId: ticket.id });
    this.host = {
      t: 0,
      ticketId: ticket.id,
      deadline: 34 * (this.options.difficulty === 'trainee' ? 1.25 : 1),
      resolved: null,
      resolvedT: 0,
    };
    this.emit({ type: 'host', phase: 'start' });
    this.banner(this.clock, 'THE WAFFLE HOUSE HAS NOTICED YOU', 'host', 3);
    this.sfx('glitch');
  }

  private updateHost(dt: number): void {
    const h = this.host;
    if (!h) return;
    if (h.resolved === null) {
      h.t += dt;
      if (h.t >= h.deadline) {
        const ticket = this.ticket(h.ticketId);
        if (ticket) this.cancelTicket(ticket, 'host');
        this.resolveHost('disappointed');
      }
    } else {
      h.resolvedT += dt;
      if (h.resolvedT >= 3) {
        const hostCustomer = [...this.customers.values()].find((c) => c.archetype === 'host');
        if (hostCustomer) this.removeCustomer(hostCustomer);
        this.host = null;
        this.emit({ type: 'host', phase: 'end', outcome: h.resolved });
      }
    }
  }

  private resolveHost(outcome: NonNullable<HostState['resolved']>): void {
    const h = this.host;
    if (!h || h.resolved) return;
    h.resolved = outcome;
    h.resolvedT = 0;
    const hostCustomer = [...this.customers.values()].find((c) => c.archetype === 'host');
    if (outcome === 'pleased') {
      this.hostPleased = true;
      this.addTips(TIPS.hostPleased, HOST_SEAT_ID, 'the host is pleased');
      this.changeIndex(INDEX.hostPleased);
      this.banner(
        'THE WAFFLE HOUSE IS PLEASED',
        'Something has changed. You feel... hired.',
        'host',
        3,
      );
      if (hostCustomer) this.say(hostCustomer.id, '...yes. YES. YOU.', 2.6, 'spooky');
      this.say('dottie', 'Baby... why are your eyes glowing?', 2.6, 'normal', 2.8);
    } else if (outcome === 'tolerant') {
      this.addTips(TIPS.hostTolerant, HOST_SEAT_ID, 'the host tolerates you');
      this.banner(
        'THE WAFFLE HOUSE IS... TOLERANT',
        'It wanted ALL THE WAY. Exactly all the way.',
        'host',
        3,
      );
      if (hostCustomer) this.say(hostCustomer.id, '...close...', 2, 'spooky');
    } else {
      this.changeIndex(INDEX.hostTimeout);
      this.banner('THE WAFFLE HOUSE WILL REMEMBER THIS', '', 'host', 2.6);
      if (hostCustomer) this.say(hostCustomer.id, '...', 1.6, 'spooky');
    }
    if (hostCustomer) {
      hostCustomer.state = 'leaving';
      hostCustomer.stateTime = 0;
    }
    this.sfx('glitch');
  }

  /* ───────────────────────────── proposal ───────────────────────────── */

  private resolveProposal(ticket: Ticket, perfect: boolean): void {
    this.proposalYes = perfect;
    this.emit({ type: 'hearts', seatId: ticket.seatId, yes: perfect });
    if (perfect) {
      const who = this.flavor.pick(['SHE', 'HE', 'THEY']);
      this.banner(`${who} SAID YES!`, 'At a Waffle House. As is tradition.', 'good', 2.8);
      this.addTips(TIPS.proposalYes, ticket.seatId, 'wedding bonus');
      this.sfx('hearts');
      this.say('crowd', '*entire restaurant applauds*', 2.2, 'happy', 0.8);
    } else {
      this.banner(
        '...THEY SAID "LET\'S TALK IN THE CAR"',
        'The hash browns were not perfect.',
        'danger',
        2.8,
      );
    }
  }

  /* ───────────────────────────── going live ───────────────────────────── */

  private startLive(streamer: Customer): void {
    if (this.live) return;
    this.live = { customerId: streamer.id, t: 0, duration: 42, viewers: 1200 };
    this.emit({ type: 'live', active: true, customerId: streamer.id });
    this.headline('LOCAL STREAMER GOES LIVE FROM WAFFLE HOUSE; CHAT "LOCKED IN"');
  }

  private updateLive(dt: number): void {
    const l = this.live;
    if (!l) return;
    l.t += dt;
    const hype = this.fight ? 9000 : this.chair ? 20000 : 700;
    l.viewers += dt * hype * this.flavor.range(0.6, 1.4);
    if (l.t >= l.duration) this.endLive();
  }

  private endLive(): void {
    const l = this.live;
    if (!l) return;
    this.stats.views += Math.round(l.viewers);
    this.live = null;
    this.emit({ type: 'live', active: false, customerId: l.customerId });
  }

  /* ───────────────────────────── the guy who cooks his own meal ───────────────────────────── */

  private checkIdle(): void {
    if (this.selfServed || this.chair || this.host?.resolved === null) return;
    if (this.minutes < 30 || this.time - this.lastInputTime < FLOW.idleSelfServeAfter) return;
    const candidate = this.openTickets().find((t) => {
      const c = this.customers.get(t.customerId);
      return (
        c && c.state === 'waiting' && t.tags.length === 0 && this.seats[t.seatId]?.kind === 'stool'
      );
    });
    if (!candidate) return;
    const c = this.customers.get(candidate.customerId);
    if (!c) return;
    this.selfServed = true;
    this.lastInputTime = this.time;
    this.cancelTicket(candidate, 'selfServe');
    c.state = 'eating';
    c.stateTime = 0;
    this.emit({ type: 'selfServe', customerId: c.id });
    this.banner(
      'YOU DOZED OFF',
      'A customer hopped the counter, cooked his own Texas Bacon Cheesesteak Melt, and cleaned the grill.',
      'sleep',
      3.6,
    );
    this.headline(
      'REPORT: CUSTOMER COOKS OWN MEAL WHILE STAFF "RESTS EYES"; CLEANS GRILL AFTERWARD',
      true,
    );
    this.say(c.id, "Y'all were asleep. I cleaned the grill too.", 3, 'happy', 1);
    this.sfx('snore');
  }

  /* ───────────────────────────── plumbing ───────────────────────────── */

  private say(
    speaker: SpeakerId,
    text: string,
    duration = 2.4,
    mood: PendingLine['mood'] = 'normal',
    delay = 0,
  ): void {
    if (delay > 0) {
      this.pendingLines.push({ at: this.time + delay, speaker, text, duration, mood });
    } else {
      this.emit({ type: 'speech', speaker, text, duration, mood });
    }
  }

  private flushLines(): void {
    if (this.pendingLines.length === 0) return;
    const due = this.pendingLines.filter((l) => l.at <= this.time);
    if (due.length === 0) return;
    this.pendingLines = this.pendingLines.filter((l) => l.at > this.time);
    for (const l of due)
      this.emit({
        type: 'speech',
        speaker: l.speaker,
        text: l.text,
        duration: l.duration,
        mood: l.mood,
      });
  }

  private banner(
    text: string,
    sub: string,
    style: Extract<GameEvent, { type: 'banner' }>['style'],
    duration: number,
  ): void {
    this.emit({ type: 'banner', text, sub, style, duration });
  }

  private headline(text: string, breaking = false): void {
    this.emit({ type: 'headline', text, breaking });
  }

  private sfx(name: Extract<GameEvent, { type: 'sfx' }>['name']): void {
    this.emit({ type: 'sfx', name });
  }

  private emit(event: GameEvent): void {
    if (this.overEmitted) return;
    if (event.type === 'over') this.overEmitted = true;
    this.events.emit(event);
  }

  /** Ends the shift (or the Waffle House). Safe to call more than once. */
  finish(reason: EndReason): void {
    if (this.status === 'over') return;
    if (this.live) this.endLive();
    if (this.storm) this.endStorm();
    this.status = 'over';
    const result: GameResult = {
      reason,
      tips: this.tips,
      index: this.index,
      stats: { ...this.stats, hourlyIndex: [...this.stats.hourlyIndex] },
      hostPleased: this.hostPleased && reason === 'shiftComplete',
      femaVerdict: this.femaVerdict,
      stormName: this.stormName,
      mode: this.options.mode,
      difficulty: this.options.difficulty,
      shiftNumber: this.options.shiftNumber,
      endedAtMinutes: this.minutes,
      proposalYes: this.proposalYes,
      secretShopper: this.selfServed,
      sawSirThisIsAWaffleHouse: this.sawSir,
      raccoonServed: this.raccoonServed,
      calmedBefore: this.options.calm?.calmed ?? null,
    };
    this.result = result;
    this.emit({ type: 'over', result });
  }
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}
